import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useParams } from "react-router-dom";
import { ChevronLeft, Calendar, Contact, LogOut, Mail, MessagesSquare, School, Settings, Users, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import UnknownRouteRedirect from "@/components/auth/UnknownRouteRedirect";
import { NotificationModal } from "@/components/modals";
import ContextSwitcher from "@/components/ContextSwitcher";
import OwnContactForm, { ContactDetailsButton } from "@/components/OwnContactForm";
import { api, errorMessage } from "@/lib/api";
import { gradeText } from "@/lib/gradeLabel";
import {
  PARENT_ANNOUNCEMENT_DISMISS_KEY,
  announcementKey,
  dismissAnnouncementKey,
  dismissAnnouncementKeys,
  loadDismissedAnnouncementKeys,
} from "@/lib/dismissedAnnouncements";
import ParentCalendar from "./ParentCalendar";
import ParentThreads from "./ParentThreads";
import ParentMessages from "./ParentMessages";
import ParentActivityThread from "./ParentActivityThread";
import { ParentSchoolDetail, ParentSchools } from "./ParentSchools";
import ParentStudentContact from "./ParentStudentContact";
import ParentStudentPassword from "./ParentStudentPassword";
import "@/styles/student.css";

type ChildRow = {
  studentUserId: string;
  studentName: string;
  courseCount: number;
};

type CourseRow = {
  id: string;
  title: string;
  activitiesDone: number;
  activitiesTotal: number;
  percent: number;
  hasPublishedWork: boolean;
  grade: {
    percent: number | null;
    letter?: string | null;
    pointsEarned: number | null;
    pointsPossible: number | null;
    awaitingCount: number;
  };
};

const TYPE_LABEL: Record<string, string> = {
  reading: "Reading",
  slides: "Slides",
  video: "Video",
  audio: "Audio",
  key_terms: "Key terms",
  homework: "Homework",
  assessment: "Assessment",
  exam: "Exam",
  project: "Project",
  lab: "Lab",
  discussion: "Discussion",
};

type ScoreRow = {
  id: string;
  type: string;
  title: string;
  percent: number | null;
  pointsEarned: number | null;
  pointsPossible: number | null;
  awaiting: boolean;
  completed: boolean;
  dueAt?: string | null;
};

type CourseScoresData = {
  student: { studentUserId: string; studentName: string };
  course: { id: string; title: string };
  activities: ScoreRow[];
};

type DueRow = {
  title: string;
  courseTitle: string | null;
  dueAt: string;
};

type ClassAnnouncement = {
  id: string;
  courseId: string;
  courseTitle: string | null;
  body: string;
  authorName: string | null;
  at: string;
};

type ChildDetail = {
  student: { studentUserId: string; studentName: string };
  courses: CourseRow[];
  due: DueRow[];
  announcements?: ClassAnnouncement[];
};

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="student-brand">
      <img
        className={compact ? "student-brand__mark" : "student-brand__logo"}
        src={compact ? "/brand/acuity-mark.png" : "/brand/acuity-logo.png"}
        alt="Acuity Learning Management System"
      />
    </div>
  );
}

function postedLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dueLabel(iso: string) {
  const due = new Date(iso);
  const date = due.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(due.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" as const }),
  });
  const days = Math.round((due.getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: `Overdue · ${date}`, late: true };
  if (days === 0) return { text: `Due today · ${date}`, late: true };
  if (days === 1) return { text: `Due tomorrow · ${date}`, late: false };
  return { text: date, late: false };
}

function StudentOverview({ studentUserId, data }: { studentUserId: string; data: ChildDetail }) {
  const { capabilities } = useAuth();
  const canSeeLinks = Boolean(
    capabilities?.viewPersonLinks ||
      capabilities?.personLinks ||
      capabilities?.viewGuardianContact ||
      capabilities?.updateGuardianContact,
  );
  const canOpenContact = Boolean(
    capabilities?.updateOwnContact ||
      capabilities?.viewStudentContact ||
      capabilities?.updateStudentContact ||
      capabilities?.viewGuardianContact ||
      capabilities?.updateGuardianContact ||
      capabilities?.viewContact ||
      capabilities?.updateContact,
  );
  const canResetPassword = Boolean(capabilities?.resetStudentPassword);
  const [links, setLinks] = useState<{ name: string; label: string; primary?: boolean; emergency?: boolean }[] | null>(null);
  const [dismissed, setDismissed] = useState(() =>
    loadDismissedAnnouncementKeys(PARENT_ANNOUNCEMENT_DISMISS_KEY),
  );

  useEffect(() => {
    if (!canSeeLinks) return undefined;
    let cancel = false;
    api<{ success: boolean; data: { name: string; label: string; primary?: boolean; emergency?: boolean }[] }>(
      `/api/parent/students/${encodeURIComponent(studentUserId)}/person-links`,
    )
      .then((env) => {
        if (!cancel) setLinks(env.data || []);
      })
      .catch(() => {
        if (!cancel) setLinks([]);
      });
    return () => {
      cancel = true;
    };
  }, [canSeeLinks, studentUserId]);

  const announcements = (data.announcements || []).filter((item) => !dismissed.has(announcementKey(item)));
  const people = links || [];
  const showLead = people.length > 0 || canOpenContact || canResetPassword;

  return (
    <div className="parent-overview">
      {showLead ? (
      <div className="parent-lead">
          {people.length > 0 ? (
            <ul className="parent-people">
              {people.map((link) => (
                <li key={`${link.label}:${link.name}`}>
                  {[link.name, link.label, link.primary ? "Primary" : "", link.emergency ? "Emergency" : ""].filter(Boolean).join(" · ")}
                </li>
              ))}
            </ul>
          ) : null}
          {canOpenContact || canResetPassword ? (
            <p className="parent-account">
              {canOpenContact ? (
                <Link
                  className="student-text-link"
                  to={
                    capabilities?.viewStudentContact ||
                    capabilities?.updateStudentContact ||
                    capabilities?.viewContact ||
                    capabilities?.updateContact
                      ? `/parent/student/${encodeURIComponent(studentUserId)}/contact`
                      : "/parent/contact"
                  }
                >
                  Contact information
                </Link>
              ) : null}
              {canResetPassword ? (
                <Link className="student-text-link" to={`/parent/student/${encodeURIComponent(studentUserId)}/password`}>
                  Reset password
                </Link>
              ) : null}
            </p>
          ) : null}
      </div>
      ) : null}

      <section className="student-card parent-announcements">
        <div className="student-section__head parent-announcements__head">
          <h2 className="parent-card-title">Announcements</h2>
          {announcements.length > 1 ? (
            <button
              type="button"
              className="student-text-link"
              onClick={() =>
                setDismissed(
                  dismissAnnouncementKeys(
                    announcements.map(announcementKey),
                    PARENT_ANNOUNCEMENT_DISMISS_KEY,
                  ),
                )
              }
            >
              Clear all
            </button>
          ) : null}
        </div>
        {announcements.length === 0 ? (
          <p className="student-row__sub">There are no announcements</p>
        ) : (
          announcements.map((item) => (
            <div key={item.id} className="student-announcement">
              <div>
                <p>{item.body}</p>
                <span className="student-row__sub">
                  {[item.courseTitle || "School announcement", item.authorName, postedLabel(item.at)].filter(Boolean).join(" · ")}
                </span>
              </div>
              <button
                type="button"
                className="student-announcement__dismiss"
                aria-label="Dismiss announcement"
                onClick={() =>
                  setDismissed(dismissAnnouncementKey(announcementKey(item), PARENT_ANNOUNCEMENT_DISMISS_KEY))
                }
              >
                <X size={14} aria-hidden />
              </button>
            </div>
          ))
        )}
      </section>

      <section className="student-card parent-classes">
        <h2 className="parent-card-title">Classes</h2>
        {data.courses.length === 0 ? (
          <p className="student-row__sub">This student is not in a class yet.</p>
        ) : (
          data.courses.map((course) => (
            <Link
              key={course.id}
              className="student-row"
              to={`/parent/student/${encodeURIComponent(studentUserId)}/course/${encodeURIComponent(course.id)}`}
            >
              <span>
                <strong>{course.title}</strong>
                <span className="student-row__sub">
                  {course.hasPublishedWork
                    ? `${course.activitiesDone} of ${course.activitiesTotal} complete · ${course.percent}%`
                    : "Nothing published yet"}
                </span>
              </span>
              <span className={course.grade.percent != null ? "parent-grade" : "parent-grade is-empty"}>
                {course.grade.percent != null ? gradeText(course.grade) : "No marks yet"}
                {course.grade.awaitingCount ? ` · ${course.grade.awaitingCount} waiting` : ""}
              </span>
            </Link>
          ))
        )}
      </section>

      <section className="student-card parent-upcoming">
        <h2 className="parent-card-title">Upcoming</h2>
        {data.due.length === 0 ? (
          <p className="student-row__sub">Nothing coming up.</p>
        ) : (
          data.due.map((item) => {
            const label = dueLabel(item.dueAt);
            return (
              <div key={`${item.courseTitle}:${item.title}:${item.dueAt}`} className="student-row">
                <span>
                  <strong>{item.title}</strong>
                  {item.courseTitle ? <span className="student-row__sub">{item.courseTitle}</span> : null}
                </span>
                <span className={`student-row__due${label.late ? " is-overdue" : ""}`}>{label.text}</span>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}

function ParentHome() {
  const [students, setStudents] = useState<ChildDetail[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    api<{ success: boolean; data: ChildRow[] }>("/api/parent/students")
      .then(async (env) => {
        const list = env.data || [];
        const details = await Promise.all(
          list.map((row) =>
            api<{ success: boolean; data: ChildDetail }>(
              `/api/parent/students/${encodeURIComponent(row.studentUserId)}`,
            ).then((next) => next.data),
          ),
        );
        if (!cancel) setStudents(details);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, []);

  if (error) {
    return (
      <div className="student-page student-page--parent">
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!students) {
    return (
      <div className="student-page student-page--parent" aria-busy="true" aria-live="polite">
        <span className="student-vh">Loading your students</span>
        <div className="student-ghost-head">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--title" />
        </div>
        <div className="student-ghost-card">
          <div className="student-ghost student-ghost--line student-ghost--w80" />
          <div className="student-ghost student-ghost--meta" />
          <div className="student-ghost student-ghost--line student-ghost--w60" />
        </div>
      </div>
    );
  }

  const only = students.length === 1 ? students[0] : null;

  return (
    <div className="student-page student-page--parent">
      <div className="student-page-head">
        <span className="student-eyebrow">Parent</span>
        <h1>{only ? only.student.studentName : "Your students"}</h1>
      </div>
      {students.length === 0 ? (
        <div className="student-empty">
          <div>
            <strong>No students linked yet</strong>
            <p>When your school links you to a student, they show up here.</p>
          </div>
        </div>
      ) : only ? (
        <StudentOverview studentUserId={only.student.studentUserId} data={only} />
      ) : (
        students.map((student) => (
          <section key={student.student.studentUserId} className="parent-student">
            <h2 className="parent-student__name">{student.student.studentName}</h2>
            <StudentOverview studentUserId={student.student.studentUserId} data={student} />
          </section>
        ))
      )}
    </div>
  );
}

function StudentDetail() {
  const { studentUserId = "" } = useParams();
  const [data, setData] = useState<ChildDetail | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data: ChildDetail }>(
      `/api/parent/students/${encodeURIComponent(studentUserId)}`,
    )
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [studentUserId]);

  if (error) {
    return (
      <div className="student-page">
        <Link className="student-text-link" to="/parent">
          <ChevronLeft size={15} /> Your students
        </Link>
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="student-page student-page--parent" aria-busy="true" aria-live="polite">
        <span className="student-vh">Loading this student</span>
        <div className="student-ghost student-ghost--link" />
        <div className="student-ghost-head">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--title" />
        </div>
        <div className="student-ghost-card">
          <div className="student-ghost student-ghost--line student-ghost--w80" />
          <div className="student-ghost student-ghost--meta" />
          <div className="student-ghost student-ghost--line student-ghost--w90" />
          <div className="student-ghost student-ghost--meta" />
        </div>
      </div>
    );
  }

  return (
    <div className="student-page student-page--parent">
      <Link className="student-text-link" to="/parent">
        <ChevronLeft size={15} /> Home
      </Link>
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">Student</span>
        <h1>{data.student.studentName}</h1>
      </div>
      <StudentOverview studentUserId={studentUserId} data={data} />
    </div>
  );
}

function scoreLabel(item: ScoreRow) {
  if (item.percent != null) return `${item.percent}%`;
  if (item.awaiting) return "Waiting";
  if (item.completed) return "Done";
  return "Not done";
}

function CourseScores() {
  const { studentUserId = "", courseId = "" } = useParams();
  const { capabilities } = useAuth();
  const canViewChat = Boolean(capabilities?.viewStudentChat);
  const [data, setData] = useState<CourseScoresData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    setData(null);
    setError("");
    api<{ success: boolean; data: CourseScoresData }>(
      `/api/parent/students/${encodeURIComponent(studentUserId)}/courses/${encodeURIComponent(courseId)}`,
    )
      .then((env) => {
        if (!cancel) setData(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [studentUserId, courseId]);

  const back = `/parent/student/${encodeURIComponent(studentUserId)}`;

  if (error) {
    return (
      <div className="student-page">
        <Link className="student-text-link" to={back}>
          <ChevronLeft size={15} /> Back
        </Link>
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="student-page student-page--parent" aria-busy="true" aria-live="polite">
        <span className="student-vh">Loading this class</span>
        <div className="student-ghost student-ghost--link" />
        <div className="student-ghost-head">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--title" />
        </div>
        <div className="student-ghost-card">
          <div className="student-ghost student-ghost--line student-ghost--w80" />
          <div className="student-ghost student-ghost--meta" />
          <div className="student-ghost student-ghost--line student-ghost--w60" />
        </div>
      </div>
    );
  }

  return (
    <div className="student-page">
      <Link className="student-text-link" to={back}>
        <ChevronLeft size={15} /> {data.student.studentName}
      </Link>
      <div className="student-page-head" style={{ marginTop: 10 }}>
        <span className="student-eyebrow">Class</span>
        <h1>{data.course.title}</h1>
        <p>Scores only. The assignment itself stays with the student.</p>
      </div>
      <section className="student-card">
        {data.activities.length === 0 ? (
          <p className="student-row__sub">Nothing has been published in this class yet.</p>
        ) : (
          data.activities.map((item) => (
            <div key={item.id} className="student-row">
              <span>
                <strong>{item.title}</strong>
                <span className="student-row__sub">
                  {TYPE_LABEL[item.type] || "Activity"}
                  {item.percent != null && item.pointsEarned != null && item.pointsPossible != null
                    ? ` · ${item.pointsEarned} of ${item.pointsPossible} points`
                    : ""}
                  {item.dueAt && !item.completed && item.percent == null && !item.awaiting
                    ? ` · ${dueLabel(item.dueAt).text}`
                    : ""}
                </span>
              </span>
              <span className="parent-score-actions">
                {canViewChat ? (
                  <Link
                    className="student-text-link"
                    to={`/parent/student/${encodeURIComponent(studentUserId)}/activity/${encodeURIComponent(item.id)}`}
                    aria-label={`Chat on ${item.title}`}
                  >
                    <MessagesSquare size={14} aria-hidden /> Chat
                  </Link>
                ) : null}
                <span
                  className="student-row__sub"
                  style={item.percent != null ? { color: "var(--s-ink)", fontWeight: 800 } : undefined}
                >
                  {scoreLabel(item)}
                </span>
              </span>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

function ParentNavButtons({
  showThreads,
  showContact,
  showMessages,
}: {
  showThreads: boolean;
  showContact: boolean;
  showMessages: boolean;
}) {
  const cls = ({ isActive }: { isActive: boolean }) => `student-navbtn${isActive ? " is-active" : ""}`;
  return (
    <>
      <NavLink to="/parent" end className={cls}>
        <Users size={18} aria-hidden />
        <span className="student-navbtn__label">Students</span>
      </NavLink>
      {showContact ? (
        <NavLink to="/parent/contact" className={cls}>
          <Contact size={18} aria-hidden />
          <span className="student-navbtn__label">Contacts</span>
        </NavLink>
      ) : null}
      {showThreads ? (
        <NavLink to="/parent/threads" className={cls}>
          <MessagesSquare size={18} aria-hidden />
          <span className="student-navbtn__label">Feedback</span>
        </NavLink>
      ) : null}
      {showMessages ? (
        <NavLink to="/parent/messages" className={cls}>
          <Mail size={18} aria-hidden />
          <span className="student-navbtn__label">Messages</span>
        </NavLink>
      ) : null}
      <NavLink to="/parent/schools" className={cls}>
        <School size={18} aria-hidden />
        <span className="student-navbtn__label">Schools</span>
      </NavLink>
      <NavLink to="/parent/calendar" className={cls}>
        <Calendar size={18} aria-hidden />
        <span className="student-navbtn__label">Calendar</span>
      </NavLink>
    </>
  );
}

export default function ParentRoot() {
  const { logout, user, capabilities } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const showThreads = Boolean(capabilities?.viewStudentChat);
  const showMessages = Boolean(capabilities?.messageStudentsOffice);
  const showContact = Boolean(
    capabilities?.updateOwnContact ||
      capabilities?.viewStudentContact ||
      capabilities?.updateStudentContact ||
      capabilities?.viewGuardianContact ||
      capabilities?.updateGuardianContact ||
      capabilities?.viewContact ||
      capabilities?.updateContact,
  );
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

  const settingsButton = (
    <button
      type="button"
      className="student-settings"
      aria-label="Settings"
      title="Settings"
      onClick={() => setSettingsOpen(true)}
    >
      <Settings size={16} aria-hidden />
    </button>
  );

  return (
    <div className="acuity-student">
      <aside className="student-nav" aria-label="Parent navigation">
        <div className="student-nav__brand-row">
          <Brand />
        </div>
        <ParentNavButtons showThreads={showThreads} showContact={showContact} showMessages={showMessages} />
        <div className="student-nav__spacer" />
        <div className="student-nav-tools">
          <button type="button" className="student-signout" onClick={logout}>
            <LogOut size={16} className="student-signout-icon" aria-hidden />
            <span className="student-signout-label">Sign out</span>
          </button>
          {settingsButton}
        </div>
      </aside>

      <div className="student-main-wrap">
        <header className="student-topbar">
          <Brand compact />
          <div className="student-topbar-actions">
            <button type="button" className="student-signout student-signout--compact" onClick={logout} aria-label="Sign out">
              <LogOut size={17} aria-hidden />
            </button>
            {settingsButton}
          </div>
        </header>
        <main className="student-main">
          <Routes>
            <Route index element={<ParentHome />} />
            {showContact ? <Route path="contact" element={<ParentStudentContact />} /> : null}
            <Route path="calendar" element={<ParentCalendar />} />
            {showMessages ? <Route path="messages" element={<ParentMessages />} /> : null}
            {showThreads ? <Route path="threads" element={<ParentThreads />} /> : null}
            {showThreads ? (
              <Route path="student/:studentUserId/activity/:activityId" element={<ParentActivityThread />} />
            ) : null}
            <Route path="schools" element={<ParentSchools />} />
            <Route path="schools/:schoolId" element={<ParentSchoolDetail />} />
            <Route path="student/:studentUserId" element={<StudentDetail />} />
            {capabilities?.updateOwnContact ||
            capabilities?.viewStudentContact ||
            capabilities?.updateStudentContact ||
            capabilities?.viewGuardianContact ||
            capabilities?.updateGuardianContact ||
            capabilities?.viewContact ||
            capabilities?.updateContact ? (
              <Route path="student/:studentUserId/contact" element={<ParentStudentContact focusStudent />} />
            ) : null}
            {capabilities?.resetStudentPassword ? (
              <Route path="student/:studentUserId/password" element={<ParentStudentPassword />} />
            ) : null}
            <Route path="student/:studentUserId/course/:courseId" element={<CourseScores />} />
            <Route path="*" element={<UnknownRouteRedirect to="/parent" />} />
          </Routes>
        </main>

        <nav className="student-bottomnav" aria-label="Parent sections">
          <ParentNavButtons showThreads={showThreads} showContact={showContact} showMessages={showMessages} />
        </nav>
      </div>

      <NotificationModal
        isOpen={settingsOpen}
        type="neutral"
        icon={<Settings />}
        title="Settings"
        message={null}
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        onClose={() => {
          setSettingsOpen(false);
          setContactOpen(false);
        }}
      >
        <div className="student-settings-body">
          {displayName || user?.email ? (
            <div className="student-settings-identity">
              {displayName ? <p className="student-settings-name">{displayName}</p> : null}
              {user?.email ? <p className="student-settings-email">{user.email}</p> : null}
              {capabilities?.updateOwnContact ? (
                <ContactDetailsButton open={contactOpen} onClick={() => setContactOpen((open) => !open)} />
              ) : null}
            </div>
          ) : null}
          <OwnContactForm open={contactOpen} />
          <ContextSwitcher variant="profile" />
        </div>
      </NotificationModal>
    </div>
  );
}
